import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, Loader2 } from "lucide-react";
import { parseErrorMessage } from "@/utils/errorMessages";
import { contactFormSchema, validateForm } from "@/utils/validationSchemas";

interface ContactReason {
  value: string;
  label: string;
}

const DEFAULT_CONTACT_REASONS: ContactReason[] = [
  { value: "Sponsorship", label: "Sponsorship" },
  { value: "Mission Partnership", label: "Mission Partnership" },
  { value: "Press Inquiry", label: "Press Inquiry" },
  { value: "Other", label: "Other" }
];

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultReason?: string;
  contactReasons?: ContactReason[];
}

const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onOpenChange,
  defaultReason,
  contactReasons = DEFAULT_CONTACT_REASONS,
}) => {
  const { organization } = useOrganization();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    reason: defaultReason || "",
    firstName: "",
    lastName: "",
    email: "",
    organization: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    
    // Client-side validation
    const validation = validateForm(contactFormSchema, formData);
    if (!validation.success) {
      setFieldErrors(validation.errors);
      const firstError = Object.values(validation.errors)[0];
      toast.error(firstError || "Please fix the errors below");
      return;
    }

    setSubmitting(true);

    try {
      // Insert into database with organization_id
      const { error: dbError } = await supabase.from("contact_submissions").insert({
        reason: formData.reason,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        organization: formData.organization,
        message: formData.message || null,
        organization_id: organization?.id || null,
      });

      if (dbError) throw dbError;

      // Send notification email
      try {
        await supabase.functions.invoke("send-contact-notification", {
          body: {
            reason: formData.reason,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            organization: formData.organization,
            message: formData.message,
          },
        });
      } catch (emailError) {
        console.error("Failed to send notification email:", emailError);
        // Don't fail the submission if email fails
      }

      setSubmitted(true);
    } catch (error: unknown) {
      console.error("Error submitting contact form:", error);
      toast.error(parseErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after dialog closes
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        reason: defaultReason || "",
        firstName: "",
        lastName: "",
        email: "",
        organization: "",
        message: "",
      });
    }, 300);
  };

  // Reset reason when defaultReason changes
  React.useEffect(() => {
    if (open && defaultReason) {
      setFormData(prev => ({ ...prev, reason: defaultReason }));
    }
  }, [open, defaultReason]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-secondary/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-secondary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-center">Message Sent!</DialogTitle>
              <DialogDescription className="text-center">
                Thank you for reaching out. We'll get back to you as soon as possible.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={handleClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Contact Us</DialogTitle>
              <DialogDescription>
                Fill out the form below and we'll get back to you shortly.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="reason">Reason for Contact *</Label>
                <Select
                  value={formData.reason}
                  onValueChange={(value) => setFormData({ ...formData, reason: value })}
                >
                  <SelectTrigger id="reason">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                <SelectContent>
                    {contactReasons.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization">Organization *</Label>
                <Input
                  id="organization"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  placeholder="Your organization name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Message (optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more about your inquiry..."
                  rows={4}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
